import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../hooks/useAuth';
import Notification from '../../../components/base/Notification';

interface ResearchDesignFormProps {
  onClose: () => void;
  onSuccess?: () => void;
  editData?: any;
}

interface StructuredDesign {
  gap_known: string;
  gap_unknown: string;
  method_name: string;
  method_reason: string;
  method_assumption: string;
  var_outcome_name: string;
  var_outcome_definition: string;
  var_outcome_example: string;
  var_treatment_name: string;
  var_treatment_definition: string;
  var_treatment_example: string;
  var_control_name: string;
  var_control_definition: string;
  var_control_example: string;
  limitation_data: string;
  limitation_method: string;
  limitation_improvement: string;
}

const defaultDesign: StructuredDesign = {
  gap_known: '',
  gap_unknown: '',
  method_name: '',
  method_reason: '',
  method_assumption: '',
  var_outcome_name: '',
  var_outcome_definition: '',
  var_outcome_example: '',
  var_treatment_name: '',
  var_treatment_definition: '',
  var_treatment_example: '',
  var_control_name: '',
  var_control_definition: '',
  var_control_example: '',
  limitation_data: '',
  limitation_method: '',
  limitation_improvement: '',
};

function parseDesign(editData: any): StructuredDesign {
  if (!editData) return { ...defaultDesign };
  
  // Check if already structured
  if (editData.gap_known !== undefined) {
    return { ...defaultDesign, ...editData };
  }
  
  // Legacy format - migrate
  return {
    ...defaultDesign,
    gap_known: editData.background_and_gap || '',
    var_outcome_name: editData.outcome_variable || '',
    var_treatment_name: editData.treatment_variable || '',
    var_control_name: editData.control_variables || '',
    method_name: editData.identification_strategy || '',
    limitation_data: editData.limitations || '',
  };
}

export default function ResearchDesignForm({ onClose, onSuccess, editData }: ResearchDesignFormProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [title, setTitle] = useState('');
  const [dataSource, setDataSource] = useState('');
  const [samplePeriod, setSamplePeriod] = useState('');
  const [structured, setStructured] = useState<StructuredDesign>({ ...defaultDesign });
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [showHelp, setShowHelp] = useState<{ [key: string]: boolean }>({});
  const [researchTheme, setResearchTheme] = useState('');
  const [availableThemes, setAvailableThemes] = useState<string[]>([]);

  useEffect(() => {
    if (editData) {
      setTitle(editData.title || '');
      setDataSource(editData.data_source || '');
      setSamplePeriod(editData.sample_period || '');
      setStructured(parseDesign(editData));
      setResearchTheme(editData.research_theme || '');
    }
    fetchAvailableThemes();
  }, [editData]);

  const fetchAvailableThemes = async () => {
    try {
      const { data, error } = await supabase
        .from('research_overview')
        .select('theme')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const themes = data?.map(item => item.theme).filter(Boolean) || [];
      setAvailableThemes(themes);
    } catch (error) {
      console.error('テーマ取得エラー:', error);
    }
  };

  const toggleHelp = (field: string) => {
    setShowHelp(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const updateField = (key: keyof StructuredDesign, value: string) => {
    setStructured(prev => ({ ...prev, [key]: value }));
  };

  const handleDelete = async () => {
    if (!editData?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('research_designs')
        .delete()
        .eq('id', editData.id);

      if (error) throw error;

      setNotification({ type: 'success', message: '研究設計を削除しました' });
      setTimeout(() => onClose(), 1500);
    } catch (error) {
      console.error('削除エラー:', error);
      setNotification({ type: 'error', message: '削除に失敗しました' });
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      setNotification({ type: 'error', message: 'ログインが必要です。再度ログインしてください。' });
      return;
    }

    if (!title || !structured.var_outcome_name || !structured.var_treatment_name) {
      setNotification({ type: 'error', message: 'タイトル、被説明変数名、説明変数名は必須です' });
      return;
    }

    setLoading(true);

    try {
      const dataToSave = {
        user_id: user.id,
        title,
        data_source: dataSource,
        sample_period: samplePeriod,
        research_theme: researchTheme || null,
        ...structured,
      };

      if (editData?.id) {
        const { error } = await supabase
          .from('research_designs')
          .update(dataToSave)
          .eq('id', editData.id);

        if (error) {
          console.error('UPDATE error:', error);
          throw new Error(`更新エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '研究設計を更新しました' });
      } else {
        const { error } = await supabase.from('research_designs').insert(dataToSave);

        if (error) {
          console.error('INSERT error:', error);
          throw new Error(`保存エラー: ${error.message}`);
        }
        setNotification({ type: 'success', message: '研究設計を保存しました' });
      }

      setTimeout(() => {
        if (onSuccess) onSuccess();
        else onClose();
      }, 1500);
    } catch (err: any) {
      console.error('保存エラー詳細:', err);
      setNotification({ type: 'error', message: `保存に失敗しました: ${err?.message || '不明なエラー'}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 md:p-8">
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      <div className="flex items-center justify-between mb-5 sm:mb-6">
        <h2 className="text-lg sm:text-xl font-bold text-slate-900 flex items-center gap-2">
          <i className="ri-flask-line text-emerald-600"></i>
          {editData ? '分析の方法を編集' : '分析の方法を追加'}
        </h2>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6">
        {/* 関連テーマ */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-semibold text-slate-700">
              関連テーマ
            </label>
          </div>
          <select
            value={researchTheme}
            onChange={(e) => setResearchTheme(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
          >
            <option value="">テーマを選択（任意）</option>
            {availableThemes.map((theme, index) => (
              <option key={index} value={theme}>{theme}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">「テーマと目的」で登録したテーマを選択できます</p>
        </div>

        {/* タイトル */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <label className="block text-sm font-semibold text-slate-700">
              設計タイトル <span className="text-red-500">*</span>
            </label>
            <button
              type="button"
              onClick={() => toggleHelp('title')}
              className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
            >
              <i className="ri-question-line text-sm"></i>
            </button>
          </div>
          
          {showHelp.title && (
            <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-700">
              <p className="text-xs">この研究設計を一言で表すタイトルをつけてください。</p>
              <p className="text-xs text-slate-600 mt-1"><strong>例：</strong>「DIDによる税制効果の推定」</p>
            </div>
          )}

          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
            placeholder="例：DIDによる税制効果の推定"
            required
          />
        </div>

        {/* 背景とギャップ */}
        <div className="border border-emerald-200 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-emerald-200 bg-gradient-to-r from-emerald-50 to-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-emerald-100 rounded-lg shrink-0">
                <i className="ri-search-eye-line text-emerald-600 text-sm sm:text-base"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">背景とギャップ</h3>
                <p className="text-xs text-slate-500">何がわかっていて、何がわかっていないか</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 space-y-4 bg-white">
            {/* 先行研究でわかっていること */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">先行研究でわかっていること</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('gap_known')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.gap_known && (
                <div className="mb-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">これまでの研究で明らかになっている事実を書きます。</p>
                  <div className="bg-white border border-emerald-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>先行研究では、税制が企業の投資行動や雇用に影響することが実証されている（田中, 2020; 佐藤, 2021）。
                  </div>
                </div>
              )}
              <textarea
                value={structured.gap_known}
                onChange={(e) => updateField('gap_known', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                rows={3}
                placeholder="例：先行研究では、税制が企業の投資行動や雇用に影響することが実証されている"
                maxLength={500}
              />
            </div>

            {/* まだわかっていないこと */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">まだわかっていないこと（ギャップ）</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('gap_unknown')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.gap_unknown && (
                <div className="mb-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">まだ検証されていない問題や、あなたの研究が埋める「空白」を書きます。</p>
                  <div className="bg-white border border-emerald-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>しかし、事業承継のタイミングへの因果効果は未検証である。
                  </div>
                </div>
              )}
              <textarea
                value={structured.gap_unknown}
                onChange={(e) => updateField('gap_unknown', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                rows={3}
                placeholder="例：しかし、事業承継のタイミングへの因果効果は未検証である"
                maxLength={500}
              />
            </div>
          </div>
        </div>

        {/* 識別戦略 */}
        <div className="border border-blue-200 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-blue-200 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-blue-100 rounded-lg shrink-0">
                <i className="ri-compass-3-line text-blue-600 text-sm sm:text-base"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">識別戦略</h3>
                <p className="text-xs text-slate-500">どんな分析手法を使い、なぜそれが適切か</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 space-y-4 bg-white">
            {/* 分析手法の名称 */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">分析手法の名称</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('method_name')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.method_name && (
                <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">使用する分析手法の名前を書きます。</p>
                  <div className="bg-white border border-blue-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>差分の差分法（Difference-in-Differences, DID）、回帰不連続デザイン（RDD）、操作変数法（IV）など
                  </div>
                </div>
              )}
              <input
                type="text"
                value={structured.method_name}
                onChange={(e) => updateField('method_name', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                placeholder="例：差分の差分法（DID）"
                maxLength={200}
              />
            </div>

            {/* なぜこの手法が適切か */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">なぜこの手法が適切か（理由）</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('method_reason')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.method_reason && (
                <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">この手法を選んだ理由を説明します。データの特性や研究の目的との適合性を書きます。</p>
                  <div className="bg-white border border-blue-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>2018年の税制改正という外生的なショックを利用できるため、因果効果の識別が可能。処置群と対照群の設定が明確である。
                  </div>
                </div>
              )}
              <textarea
                value={structured.method_reason}
                onChange={(e) => updateField('method_reason', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={3}
                placeholder="例：2018年の税制改正という外生的なショックを利用できるため、因果効果の識別が可能"
                maxLength={500}
              />
            </div>

            {/* 手法が成立するための前提条件 */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">手法が成立するための前提条件</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('method_assumption')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 hover:bg-blue-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.method_assumption && (
                <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">この手法が正しく機能するために必要な仮定や条件を書きます。</p>
                  <div className="bg-white border border-blue-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>平行トレンド仮定が成立すること（処置前の両群のトレンドが同じ）。SUTVA（安定的単位処置値仮定）が満たされること。
                  </div>
                </div>
              )}
              <textarea
                value={structured.method_assumption}
                onChange={(e) => updateField('method_assumption', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                rows={3}
                placeholder="例：平行トレンド仮定が成立すること（処置前の両群のトレンドが同じ）"
                maxLength={500}
              />
            </div>
          </div>
        </div>

        {/* 変数辞書 */}
        <div className="border border-violet-200 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-violet-200 bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-violet-100 rounded-lg shrink-0">
                <i className="ri-database-2-line text-violet-600 text-sm sm:text-base"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">変数辞書</h3>
                <p className="text-xs text-slate-500">分析で使う変数の定義と具体例</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 space-y-5 bg-white">
            {/* 被説明変数 */}
            <div className="bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                <i className="ri-arrow-right-circle-line text-violet-600"></i>
                被説明変数（Y）
              </h4>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">変数名 <span className="text-red-500">*</span></label>
                    <button
                      type="button"
                      onClick={() => toggleHelp('var_outcome_name')}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                  {showHelp.var_outcome_name && (
                    <div className="mb-2 p-2 bg-white border border-violet-200 rounded text-xs text-slate-700">
                      <strong>例：</strong>事業承継の実施、承継までの年数、企業の生産性
                    </div>
                  )}
                  <input
                    type="text"
                    value={structured.var_outcome_name}
                    onChange={(e) => updateField('var_outcome_name', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    placeholder="例：事業承継の実施"
                    required
                    maxLength={200}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">定義（何をどう測るか）</label>
                    <button
                      type="button"
                      onClick={() => toggleHelp('var_outcome_def')}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                  {showHelp.var_outcome_def && (
                    <div className="mb-2 p-2 bg-white border border-violet-200 rounded text-xs text-slate-700">
                      <strong>例：</strong>経営者が交代した場合を1、交代していない場合を0とするダミー変数
                    </div>
                  )}
                  <textarea
                    value={structured.var_outcome_definition}
                    onChange={(e) => updateField('var_outcome_definition', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    rows={2}
                    placeholder="例：経営者が交代した場合を1、交代していない場合を0とするダミー変数"
                    maxLength={300}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">データ上の具体的な値の例</label>
                    <button
                      type="button"
                      onClick={() => toggleHelp('var_outcome_ex')}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                  {showHelp.var_outcome_ex && (
                    <div className="mb-2 p-2 bg-white border border-violet-200 rounded text-xs text-slate-700">
                      <strong>例：</strong>0または1、平均値0.15（サンプルの15%が承継を実施）
                    </div>
                  )}
                  <input
                    type="text"
                    value={structured.var_outcome_example}
                    onChange={(e) => updateField('var_outcome_example', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    placeholder="例：0または1、平均値0.15（サンプルの15%が承継を実施）"
                    maxLength={200}
                  />
                </div>
              </div>
            </div>

            {/* 説明変数 */}
            <div className="bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                <i className="ri-arrow-left-circle-line text-violet-600"></i>
                説明変数（X）
              </h4>
              <div className="space-y-3">
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">変数名 <span className="text-red-500">*</span></label>
                    <button
                      type="button"
                      onClick={() => toggleHelp('var_treatment_name')}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                  {showHelp.var_treatment_name && (
                    <div className="mb-2 p-2 bg-white border border-violet-200 rounded text-xs text-slate-700">
                      <strong>例：</strong>事業承継税制の適用、相続税負担率、補助金の受給
                    </div>
                  )}
                  <input
                    type="text"
                    value={structured.var_treatment_name}
                    onChange={(e) => updateField('var_treatment_name', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    placeholder="例：事業承継税制の適用"
                    required
                    maxLength={200}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">定義（何をどう測るか）</label>
                    <button
                      type="button"
                      onClick={() => toggleHelp('var_treatment_def')}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                  {showHelp.var_treatment_def && (
                    <div className="mb-2 p-2 bg-white border border-violet-200 rounded text-xs text-slate-700">
                      <strong>例：</strong>事業承継税制の特例措置を適用した場合を1、適用していない場合を0とするダミー変数
                    </div>
                  )}
                  <textarea
                    value={structured.var_treatment_definition}
                    onChange={(e) => updateField('var_treatment_definition', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    rows={2}
                    placeholder="例：事業承継税制の特例措置を適用した場合を1、適用していない場合を0とするダミー変数"
                    maxLength={300}
                  />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1.5">
                    <label className="text-xs font-semibold text-slate-700">データ上の具体的な値の例</label>
                    <button
                      type="button"
                      onClick={() => toggleHelp('var_treatment_ex')}
                      className="w-4 h-4 flex items-center justify-center rounded-full bg-violet-100 text-violet-600 hover:bg-violet-200 transition-colors cursor-pointer"
                    >
                      <i className="ri-question-line text-xs"></i>
                    </button>
                  </div>
                  {showHelp.var_treatment_ex && (
                    <div className="mb-2 p-2 bg-white border border-violet-200 rounded text-xs text-slate-700">
                      <strong>例：</strong>0または1、処置群の平均値1.0、対照群の平均値0.0
                    </div>
                  )}
                  <input
                    type="text"
                    value={structured.var_treatment_example}
                    onChange={(e) => updateField('var_treatment_example', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    placeholder="例：0または1、処置群の平均値1.0、対照群の平均値0.0"
                    maxLength={200}
                  />
                </div>
              </div>
            </div>

            {/* 統制変数 */}
            <div className="bg-slate-50 p-3 sm:p-4 rounded-lg border border-slate-200">
              <h4 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2">
                <i className="ri-equalizer-line text-violet-600"></i>
                統制変数（Z）
              </h4>
              <div className="space-y-3">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">変数名</label>
                  <input
                    type="text"
                    value={structured.var_control_name}
                    onChange={(e) => updateField('var_control_name', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    placeholder="例：企業規模、業種、地域、経営者年齢"
                    maxLength={200}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">定義（何をどう測るか）</label>
                  <textarea
                    value={structured.var_control_definition}
                    onChange={(e) => updateField('var_control_definition', e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    rows={2}
                    placeholder="例：企業規模は従業員数の対数値、業種は日本標準産業分類の中分類ダミー、地域は都道府県ダミー"
                    maxLength={300}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">データ上の具体的な値の例</label>
                  <input
                    type="text"
                    value={structured.var_control_example}
                    onChange={(e) => updateField('var_control_example', e.target.value)}
                    className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent text-base"
                    placeholder="例：従業員数の対数値は3.5〜7.2の範囲、業種ダミーは製造業=1その他=0"
                    maxLength={200}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* データソースとサンプル期間 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">データソース</label>
              <button
                type="button"
                onClick={() => toggleHelp('data')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            {showHelp.data && (
              <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-700">
                <p>使用するデータの名称と入手先を記入してください。</p>
                <p className="text-slate-600 mt-1"><strong>例：</strong>企業活動基本調査（経済産業省）</p>
              </div>
            )}
            <input
              type="text"
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
              placeholder="例：企業活動基本調査（経済産業省）"
            />
          </div>

          <div>
            <div className="flex items-center gap-2 mb-2">
              <label className="block text-sm font-semibold text-slate-700">サンプル期間</label>
              <button
                type="button"
                onClick={() => toggleHelp('period')}
                className="w-5 h-5 flex items-center justify-center rounded-full bg-emerald-100 text-emerald-600 hover:bg-emerald-200 transition-colors cursor-pointer"
              >
                <i className="ri-question-line text-sm"></i>
              </button>
            </div>
            {showHelp.period && (
              <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-slate-700">
                <p>分析対象とする期間とサンプルサイズを記入してください。</p>
                <p className="text-slate-600 mt-1"><strong>例：</strong>2010-2020年、約5,000社</p>
              </div>
            )}
            <input
              type="text"
              value={samplePeriod}
              onChange={(e) => setSamplePeriod(e.target.value)}
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
              placeholder="例：2010-2020年、約5,000社"
            />
          </div>
        </div>

        {/* 限界 */}
        <div className="border border-rose-200 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-rose-200 bg-gradient-to-r from-rose-50 to-white">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-rose-100 rounded-lg shrink-0">
                <i className="ri-alert-line text-rose-600 text-sm sm:text-base"></i>
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800">限界</h3>
                <p className="text-xs text-slate-500">この分析の制約と今後の改善点</p>
              </div>
            </div>
          </div>
          <div className="p-4 sm:p-5 space-y-4 bg-white">
            {/* データの制約 */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">データの制約</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('limit_data')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.limit_data && (
                <div className="mb-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">データに関する制約や問題点を書きます。</p>
                  <div className="bg-white border border-rose-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>サンプル期間が10年と短く、長期的な効果は不明。中小企業のみが対象で大企業への一般化は困難。
                  </div>
                </div>
              )}
              <textarea
                value={structured.limitation_data}
                onChange={(e) => updateField('limitation_data', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
                rows={2}
                placeholder="例：サンプル期間が10年と短く、長期的な効果は不明"
                maxLength={300}
              />
            </div>

            {/* 手法の限界 */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">手法の限界</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('limit_method')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.limit_method && (
                <div className="mb-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">分析手法に関する限界や注意点を書きます。</p>
                  <div className="bg-white border border-rose-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>平行トレンド仮定が完全には検証できない。選択バイアスが残る可能性がある。
                  </div>
                </div>
              )}
              <textarea
                value={structured.limitation_method}
                onChange={(e) => updateField('limitation_method', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
                rows={2}
                placeholder="例：平行トレンド仮定が完全には検証できない。選択バイアスが残る可能性がある"
                maxLength={300}
              />
            </div>

            {/* 今後の改善策 */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="text-sm font-semibold text-slate-700">今後の改善策</label>
                <button
                  type="button"
                  onClick={() => toggleHelp('limit_improve')}
                  className="w-5 h-5 flex items-center justify-center rounded-full bg-rose-100 text-rose-600 hover:bg-rose-200 transition-colors cursor-pointer"
                >
                  <i className="ri-question-line text-xs"></i>
                </button>
              </div>
              {showHelp.limit_improve && (
                <div className="mb-2 p-3 bg-rose-50 border border-rose-200 rounded-lg text-xs text-slate-700">
                  <p className="mb-1">今後どのように改善できるかを書きます。</p>
                  <div className="bg-white border border-rose-200 rounded p-2 text-slate-600">
                    <strong>例：</strong>より長期のデータで検証する。他の識別戦略（RDDなど）と組み合わせる。
                  </div>
                </div>
              )}
              <textarea
                value={structured.limitation_improvement}
                onChange={(e) => updateField('limitation_improvement', e.target.value)}
                className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-rose-500 focus:border-transparent text-base"
                rows={2}
                placeholder="例：より長期のデータで検証する。他の識別戦略（RDDなど）と組み合わせる"
                maxLength={300}
              />
            </div>
          </div>
        </div>

        {/* ボタン */}
        <div className="flex flex-col sm:flex-row gap-3 pt-4">
          {editData && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="w-full sm:w-auto px-6 py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              削除
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full sm:flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            キャンセル
          </button>
          <button
            type="submit"
            disabled={loading}
            className="w-full sm:flex-1 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
          >
            {loading ? '保存中...' : editData ? '更新する' : '保存'}
          </button>
        </div>
      </form>

      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-white rounded-xl p-5 sm:p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2">削除の確認</h3>
            <p className="text-slate-600 mb-6">この研究設計を削除してもよろしいですか？この操作は取り消せません。</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="w-full sm:flex-1 px-4 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                キャンセル
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="w-full sm:flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer"
              >
                {loading ? '削除中...' : '削除する'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
